import { defineMessages, useIntl } from 'react-intl'
import { FormControl, Switch, FormLabel, Box, Flex, Icon, Card, Text } from '@homebotapp/hb-react-component-catalog'
import { AssumablesLearnMoreModal } from '../../AssumablesLearnMoreModal/AssumablesLearnMoreModal'
import { selectCustomerProfile } from '../../../../store/selectors/customerProfile'
import { useSelector } from 'react-redux'
import { AssumablesDifficultyNotice } from '../../AssumablesLearnMoreModal/AssumablesDifficultyNotice'
import { formatAbbrCurrency } from '../../../../helpers/formatters'

export const MSG = defineMessages({
  calculateWithAssumable: {
    id: 'estimatedMonthlyPayment.calculateWithAssumable',
    defaultMessage: 'Calculate with assumable loan'
  },
  info: {
    id: 'estimatedMonthlyPayment.info',
    defaultMessage: 'Info'
  },
  noSavings: {
    id: 'estimatedMonthlyPayment.noSavings',
    defaultMessage: 'Likely no savings for you with the current rate, downpayment and details selected.'
  },
  perMonth: {
    id: 'estimatedMonthlyPayment.perMonth',
    defaultMessage: '{amount}/mo'
  },
  possibleSavings: {
    id: 'estimatedMonthlyPayment.possibleSavings',
    defaultMessage: 'Possible savings'
  }
})

export interface AssumableSwitchProps {
  isAssumable: boolean
  assumableToggle?: boolean
  buyDownToggle?: boolean
  potentialAssumableSavings: number | null
  setBuyDownToggle?: (value: boolean) => void
  setAssumableToggle?: (value: boolean) => void
  zipcode?: string
}

export const AssumablePaymentToggle = ({
  isAssumable,
  assumableToggle,
  potentialAssumableSavings,
  setAssumableToggle,
  setBuyDownToggle,
  zipcode
}: AssumableSwitchProps) => {
  const intl = useIntl()
  const cp = useSelector(selectCustomerProfile)

  if (!cp?.assumableMortgagesEnabled) {
    return null
  }

  const handleAssumableToggle = (checked: boolean) => {
    setAssumableToggle && setAssumableToggle(checked)

    setBuyDownToggle(false)
  }

  return (
    <>
      {isAssumable && (
        <Card>
          <Flex justify='space-between' align='center' flexDir={'row'} mx={4}>
            <FormControl display='flex' alignItems='center'>
              <Flex align='center' gap={4} my={2}>
                <Switch
                  mb={0}
                  name='assumableSwitch'
                  isChecked={assumableToggle}
                  onChange={e => handleAssumableToggle(e.target.checked)}
                  tracking={{
                    guid: 'dgck1FLxDoVmr1CsyAsqH1',
                    ui_context: 'Listing details',
                    descriptor: 'Toggled assumable switch for monthly payment details'
                  }}
                />
                <FormLabel mb={0} display='flex' alignItems='center'>
                  {intl.formatMessage(MSG.calculateWithAssumable)}
                </FormLabel>
              </Flex>
            </FormControl>
            <AssumablesLearnMoreModal
              buttonProps={{
                variant: 'ghost',
                colorScheme: 'primary',
                size: 'xs',
                copy: intl.formatMessage(MSG.info),
                tracking: {
                  guid: 'xiZjp3xrD4ZHgLChjD8H2Z',
                  ui_context: 'Listing Details, assumable payment information toggle',
                  descriptor: 'Info button to learn more about assumables'
                }
              }}
              zipcode={zipcode}
            />
          </Flex>
          {assumableToggle && (
            <Box m={4}>
              {potentialAssumableSavings > 0 ? (
                <>
                  <Text color='success.700' fontWeight='bold' size='lg' textAlign='center' mb={0}>
                    {intl.formatMessage(MSG.perMonth, { amount: formatAbbrCurrency(potentialAssumableSavings) })}
                  </Text>
                  <Text color='neutral.700' size='sm' textAlign='center'>
                    {intl.formatMessage(MSG.possibleSavings)}
                  </Text>
                </>
              ) : (
                <Text color='warning.800' textAlign='center' fontWeight={'semibold'} size='sm'>
                  {intl.formatMessage(MSG.noSavings)}
                </Text>
              )}
              <AssumablesDifficultyNotice />
            </Box>
          )}
        </Card>
      )}
    </>
  )
}
